/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * Formula rendering.
 *
 * KaTeX and its auto-render extension were script tags on every map page,
 * whether or not anything on it was a formula — around 65 kB gzipped of
 * script and stylesheet that most maps never use. They are loaded here
 * instead, on the first element that actually contains maths.
 *
 * The `$…$` delimiter is enabled only where the text really contains inline
 * maths. Turning it on unconditionally makes KaTeX try to render every
 * price in a sentence mentioning two amounts.
 */

const INLINE_DOLLAR_MATH = /(?:^|[^$])\$[^\s$][^$]*\$(?!\d)/;
const BRACKET_MATH = /\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)/;

/**
 * Detects which kinds of TeX math an element's text contains.
 * @param elem - A DOM element
 * @returns - `{ any, inlineDollar }`; `any` is true when any math is present,
 *   `inlineDollar` is true only when a genuine `$…$` formula (not currency)
 *   is present.
 * @type {(elem: Object) => { any: boolean, inlineDollar: boolean }}
 */
export const detectMath = (elem) => {
    const none = { any: false, inlineDollar: false };
    if (!elem) return none;
    try {
        const txt = elem.textContent || '';
        const inlineDollar = INLINE_DOLLAR_MATH.test(txt);
        return { any: inlineDollar || BRACKET_MATH.test(txt), inlineDollar };
    } catch (_) {
        return none;
    }
};

/**
 * Checks if a given DOM element contains a TeX formula.
 * @param elem - A DOM element
 * @returns - `true` if the element contains a mathematical formula
 * @type {(elem: Object) => boolean}
 */
export const containsMath = (elem) => detectMath(elem).any;

/**
 * Converts mathematical formulas in TeX format.
 * This requires KaTeX (katex.min.js) and its auto-render extension (auto-render.min.js).
 * The bare ``$`` delimiter is enabled only when genuine inline math is
 * detected, so currency in mixed text is never handed to KaTeX.
 * @param elem - A DOM element
 * @type {(elem: Object) => undefined}
 */
let katexLoading = null;

/**
 * Load KaTeX and its stylesheet, once.
 *
 * @returns {Promise<Function>} The auto-render entry point.
 */
function loadKatex() {
    if (!katexLoading) {
        katexLoading = Promise.all([
            import('katex/contrib/auto-render'),
            import('katex/dist/katex.min.css'),
        ]).then(([module]) => module.default);
    }
    return katexLoading;
}

export const renderMath = async (elem) => {
    const math = detectMath(elem);
    if (!math.any) {
        return;
    }

    const delimiters = [
        { left: '$$', right: '$$', display: true },
        { left: '\\[', right: '\\]', display: true },
        { left: '\\(', right: '\\)', display: false }
    ];
    if (math.inlineDollar) {
        delimiters.push({ left: '$', right: '$', display: false });
    }

    const renderMathInElement = await loadKatex();
    renderMathInElement(elem, { delimiters, throwOnError: false });
};
